import html2canvas from 'html2canvas';

/**
 * Options for capturing chart elements
 */
export interface ChartCaptureOptions {
  /** Scale factor for high-DPI displays (default: 2) */
  scale?: number;
  /** Background color for transparent charts (default: '#ffffff') */
  backgroundColor?: string;
  /** Maximum width in pixels (default: 1200) */
  maxWidth?: number;
  /** Maximum height in pixels (default: 800) */
  maxHeight?: number;
  /** Enable logging for debugging (default: false) */
  logging?: boolean;
  /** Number of retry attempts on failure (default: 1) */
  retryAttempts?: number;
}

/**
 * Result of chart capture operation
 */
export interface ChartCaptureResult {
  /** Base64 encoded image data (data URL) */
  dataUrl: string;
  /** Image width in pixels */
  width: number;
  /** Image height in pixels */
  height: number;
  /** Whether capture was successful */
  success: boolean;
  /** Error message if capture failed */
  error?: string;
}

/**
 * Delay execution for specified milliseconds
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Capture a chart element as an image using html2canvas
 *
 * @param element - DOM element to capture (can be HTMLElement or string selector)
 * @param options - Capture options
 * @returns Promise resolving to capture result
 */
export async function captureChartAsImage(
  element: HTMLElement | string,
  options: ChartCaptureOptions = {}
): Promise<ChartCaptureResult> {
  const {
    scale = 1.5,
    backgroundColor = '#ffffff',
    maxWidth = 1200,
    maxHeight = 800,
    logging = false,
    retryAttempts = 1,
  } = options;

  // Resolve element
  let targetElement: HTMLElement | null = null;
  if (typeof element === 'string') {
    targetElement = document.querySelector(element);
    if (!targetElement) {
      return {
        dataUrl: '',
        width: 0,
        height: 0,
        success: false,
        error: `Element not found: ${element}`,
      };
    }
  } else {
    targetElement = element;
  }

  // Capture with retry logic
  for (let attempt = 0; attempt <= retryAttempts; attempt++) {
    try {
      if (logging) {
        console.log(`[ChartCapture] Attempt ${attempt + 1}/${retryAttempts + 1} to capture element`);
      }

      // Wait a bit for any animations to complete
      await delay(200);

      // Capture the element
      const canvas = await html2canvas(targetElement, {
        scale,
        backgroundColor,
        useCORS: true,
        allowTaint: true,
        logging: logging,
        width: targetElement.scrollWidth || targetElement.offsetWidth,
        height: targetElement.scrollHeight || targetElement.offsetHeight,
        windowWidth: targetElement.scrollWidth || targetElement.offsetWidth,
        windowHeight: targetElement.scrollHeight || targetElement.offsetHeight,
        onclone: (clonedDoc) => {
          // Fix for Recharts SVG elements
          const clonedElement = clonedDoc.querySelector(`[data-chart-id]`) ||
                                 clonedDoc.body.querySelector('.recharts-wrapper');
          if (clonedElement) {
            // Ensure SVG elements render correctly
            const svgElements = clonedElement.querySelectorAll('svg');
            svgElements.forEach((svg) => {
              svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            });
          }
        },
      });

      // Convert canvas to data URL.
      // JPEG (vs PNG) keeps chart images small enough to email: charts render on a
      // solid white background, so JPEG compression is visually lossless here while
      // cutting file size by roughly an order of magnitude (~60 MB PDFs were caused
      // by uncompressed PNG chart captures at high scale).
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

      if (logging) {
        console.log(`[ChartCapture] Successfully captured chart: ${canvas.width}x${canvas.height}`);
      }

      return {
        dataUrl,
        width: canvas.width,
        height: canvas.height,
        success: true,
      };
    } catch (error) {
      if (logging) {
        console.error(`[ChartCapture] Attempt ${attempt + 1} failed:`, error);
      }

      // If this was the last attempt, return error
      if (attempt === retryAttempts) {
        return {
          dataUrl: '',
          width: 0,
          height: 0,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error capturing chart',
        };
      }

      // Wait before retrying
      await delay(200);
    }
  }

  // Should never reach here, but TypeScript needs it
  return {
    dataUrl: '',
    width: 0,
    height: 0,
    success: false,
    error: 'Unexpected error in chart capture',
  };
}

/**
 * Capture multiple charts in parallel
 *
 * @param elements - Array of elements or selectors to capture
 * @param options - Capture options
 * @returns Promise resolving to array of capture results
 */
export async function captureMultipleCharts(
  elements: (HTMLElement | string)[],
  options: ChartCaptureOptions = {}
): Promise<ChartCaptureResult[]> {
  const capturePromises = elements.map(element => captureChartAsImage(element, options));
  return Promise.all(capturePromises);
}

/**
 * Prepare a chart element for PDF export by adding data attributes
 * Call this before rendering charts that need to be captured
 *
 * @param chartId - Unique identifier for the chart
 * @returns Data attribute to add to chart container
 */
export function prepareChartForCapture(chartId: string): { 'data-chart-id': string } {
  return { 'data-chart-id': chartId };
}

/**
 * Chart capture service with common methods
 */
export const chartCaptureService = {
  captureChartAsImage,
  captureMultipleCharts,
  prepareChartForCapture,
};
